import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { range } from 'ramda';
import { EDStickyTable } from '../../StickyTable/EDStickyTable';
import { EDStickyTableHeader } from '../../StickyTable/Components/EDStickyTableHeader';
import { EDStickyTableHeaderStickyCell } from '../../StickyTable/Components/EDStickyTableHeaderStickyCell';
import { EDStickyTableHeaderCell } from '../../StickyTable/Components/EDStickyTableHeaderCell';
import { EDStickyTableRows } from '../../StickyTable/Components/EDStickyTableRows';
import { EDStickyTableRow } from '../../StickyTable/Components/EDStickyTableRow';
import { EDStickyTableStickyCell } from '../../StickyTable/Components/EDStickyTableStickyCell';
import {
  EDStickyTableCell,
  EDStickyTableCellSelectedEvent,
  EDStickyTableCellSelectionClickEvent,
} from '../../StickyTable/Components/EDStickyTableCell';
import {
  incDay,
  getDate,
  incMin,
  firstDayInWeek,
  decMin,
  dateAtTime,
} from '../../../Utils/dateUtils';
import {
  ICalendarViewProps,
  timeRangeOverlap,
  updateSelection,
  IBooking,
  prepareViewBookings,
} from '../Utils/EDCalendarViewUtils';
import { dateToStr } from '../../../Utils/stringUtils';
import { EDCalendarViewTimeViewBooking } from './EDCalendarViewTimeViewBooking';
import { EDCalendarViewWeekNumberBox } from './EDCalendarViewWeekNumberBox';
import { EDCalendarViewDayCell } from './EDCalendarViewDayCell';
import { EDCalendarViewClockCell } from './EDCalendarViewClockCell';
import { KalenderAllDayBookings } from '../../../Features/Delsystem/Bokning/Rutiner/Kalender/Components/KalenderAllDayBookings';
import EDIconButton from '../../Buttons/IconButton/EDIconButton';
import EDBox from '../../EDBox/EDBox';
import { useIsSmallScreen } from '../../../Hooks/useFullscreenView';

export const EDCalendarViewTimeView: React.FC<ICalendarViewProps> = props => {
  const dayCount = props.dayCount ?? 7;
  const isSmallScreen = useIsSmallScreen();
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const WORK_HOUR_START = 8;
  const WORK_HOUR_END = 24;
  const TIME_ROWS = 48; // Ett dygn i halvtimmar
  const TIME_WIDTH = 60;
  const MIN_TIME_HEIGHT = 25;
  const VISIBLE_TIME_ROWS = isSmallScreen ? 20 : 34; // Synliga halvtimmar
  const MIN_DAY_WIDTH = useIsSmallScreen()
    ? Math.floor((props.width - TIME_WIDTH) / (dayCount + 1))
    : 120;

  const HEAD_HEIGHT = useIsSmallScreen() ? 60 : 90;

  const viewStartTime = useMemo(
    () =>
      dayCount > 1
        ? firstDayInWeek(getDate(props.currentDate))
        : getDate(props.currentDate),
    [dayCount, props.currentDate]
  );
  const viewEndTime = useMemo(
    () => incDay(viewStartTime, dayCount),
    [viewStartTime, dayCount]
  );

  // Expandera/minimera headerav
  const toggleHeader = useCallback(() => {
    setIsHeaderExpanded(prev => !prev);
  }, []);

  // Calculate fallback dates based on current view
  const fallbackDates = useMemo(() => {
    const startFallback = new Date(viewStartTime);
    startFallback.setHours(12, 0, 0, 0);

    const endFallback = new Date(viewStartTime);
    endFallback.setDate(viewStartTime.getDate() + Math.min(dayCount - 1, 6));
    endFallback.setHours(15, 0, 0, 0);

    return {
      start: startFallback.toISOString(),
      end: endFallback.toISOString(),
    };
  }, [viewStartTime, dayCount]);

  // Filtrera bokningar efter aktuell view
  const bookingsInCurrentView = useMemo(() => {
    return (props.allDayBookings || []).filter(booking => {
      const bookingStart = new Date(
        booking.aktStartTime || fallbackDates.start
      );
      const bookingEnd = new Date(booking.aktEndTime || fallbackDates.end);

      const startDayDiff = Math.floor(
        (bookingStart.getTime() - viewStartTime.getTime()) /
          (1000 * 60 * 60 * 24)
      );
      const endDayDiff = Math.floor(
        (bookingEnd.getTime() - viewStartTime.getTime()) / (1000 * 60 * 60 * 24)
      );
      const startDayIndex = Math.max(0, startDayDiff);
      const endDayIndex = Math.min(dayCount - 1, endDayDiff);
      const spanDays = endDayIndex - startDayIndex + 1;

      return spanDays > 0 && startDayIndex < dayCount && endDayIndex >= 0;
    });
  }, [
    props.allDayBookings,
    fallbackDates.start,
    fallbackDates.end,
    viewStartTime,
    dayCount,
  ]);

  // Beräkna synliga flerdagsvisningar
  const visibleBookings = useMemo(() => {
    const maxCollapsedBookings = isSmallScreen ? 1 : 2; // 1 för små skärmar, 2 för större

    if (
      !isHeaderExpanded &&
      bookingsInCurrentView.length > maxCollapsedBookings
    ) {
      return bookingsInCurrentView.slice(0, maxCollapsedBookings);
    }
    return bookingsInCurrentView;
  }, [bookingsInCurrentView, isHeaderExpanded, isSmallScreen]);

  // Beräkna höjd på header baserat på hur många bokningar som syns
  const visibleBookingCount = visibleBookings.length;
  const additionalHeight = visibleBookingCount * 25;
  const dynamicHeaderHeight = HEAD_HEIGHT + additionalHeight;

  const getHourHeight = (isSmallScreen: boolean): number => {
    if (isSmallScreen) {
      return Math.floor((props.height - HEAD_HEIGHT) / VISIBLE_TIME_ROWS);
    }
    return Math.max(
      MIN_TIME_HEIGHT,
      Math.floor((props.height - HEAD_HEIGHT) / VISIBLE_TIME_ROWS)
    );
  };

  const hourHeight = getHourHeight(isSmallScreen);

  const dayWidth = Math.max(
    MIN_DAY_WIDTH,
    Math.floor((props.width - TIME_WIDTH) / dayCount)
  );
  const dayWidthForAllDayBookings = Math.max(
    MIN_DAY_WIDTH,
    Math.floor((props.width - TIME_WIDTH) / dayCount)
  );
  const scrollAreaHeight = hourHeight * TIME_ROWS;
  const initialScroll = WORK_HOUR_START * 2 - 1;

  // Hantera bokningar
  const bookingColumns = useMemo(
    () =>
      prepareViewBookings(
        props.regularBookings,
        viewStartTime,
        viewEndTime,
        booking => dateToStr(booking.startTime),
        true
      ),
    [props.regularBookings, viewStartTime, viewEndTime]
  );

  // Hantera markering
  const selStart = useMemo(() => props.selStart, [props.selStart]);
  const selEnd = useMemo(() => props.selEnd, [props.selEnd]);
  const selResources = useMemo(() => props.selResources, [props.selResources]);
  const allResIds = useMemo(
    () => props.resources.map(res => res.id),
    [props.resources]
  );

  const onSelectionChange = props.onSelectionChange;
  const onSelectionClick = props.onSelectionClick;
  const onBookingClick = props.onBookingClick;

  const handleCellSelected: EDStickyTableCellSelectedEvent = useCallback(
    (
      day: number,
      halfHour: number,
      shift: boolean,
      _ctrl: boolean,
      drag: boolean
    ) => {
      const cellStartTime = dateAtTime(
        incDay(viewStartTime, day),
        Math.floor(halfHour / 2),
        30 * (halfHour % 2)
      );
      if (onSelectionChange) {
        if ((shift || drag) && selStart && selEnd) {
          setIsDragging(drag);
          updateSelection(
            selStart,
            cellStartTime,
            allResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        } else {
          updateSelection(
            cellStartTime,
            cellStartTime,
            allResIds,
            selStart,
            selEnd,
            selResources,
            onSelectionChange
          );
        }
      }
    },
    [
      viewStartTime,
      allResIds,
      selStart,
      selEnd,
      selResources,
      onSelectionChange,
    ]
  );

  const handleSelectionClick: EDStickyTableCellSelectionClickEvent =
    useCallback(
      ref => {
        if (onSelectionClick) {
          onSelectionClick(ref, selStart, selEnd, selResources);
        }
      },
      [selStart, selEnd, selResources, onSelectionClick]
    );

  const IS_SMALL_SCREEN = useIsSmallScreen();

  useEffect(() => {
    const handleMouseDown = () => setIsDragging(false);
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);
  return (
    <EDStickyTable
      width={props.width}
      height={props.height}
      colCount={dayCount + 1}
      rowCount={TIME_ROWS + 1}
      minColWidth={MIN_DAY_WIDTH}
      firstColWidth={TIME_WIDTH}
      firstColSticky={true}
      minRowHeight={hourHeight}
      firstRowHeight={dynamicHeaderHeight}
      firstRowSticky={true}
      firstRowAlignment={dayCount === 1 ? 'left' : undefined}
      firstRowDirection="row"
      initialScrollToRow={initialScroll}
      cellAllowOverflow={true}
      firstColAllowOverflow={true}
      bottomSpacing={props.bottomSpacing}
    >
      <EDStickyTableHeader>
        <EDStickyTableHeaderStickyCell>
          <EDBox
            sx={{
              position: 'absolute',
              top: `${HEAD_HEIGHT * 0.05}px`,
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            <EDCalendarViewWeekNumberBox date={viewStartTime} />
          </EDBox>

          {bookingsInCurrentView &&
            bookingsInCurrentView.length > (isSmallScreen ? 1 : 2) && (
              <EDBox
                sx={{
                  position: 'absolute',
                  top: `${HEAD_HEIGHT * 0.5}px`,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                <EDIconButton
                  icon={isHeaderExpanded ? 'arrow_drop_up' : 'arrow_drop_down'}
                  tooltip={
                    isHeaderExpanded
                      ? 'Dölj flerdagsbokningar'
                      : 'Visa alla flerdagsbokningar'
                  }
                  onClick={toggleHeader}
                  color="primary"
                  iconSize="small"
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: '50%',
                  }}
                />
              </EDBox>
            )}
        </EDStickyTableHeaderStickyCell>
        {range(0, dayCount).map(day => (
          // Titelrad med veckodagar
          <EDStickyTableHeaderCell key={day}>
            <EDCalendarViewDayCell
              isSmallScreen={IS_SMALL_SCREEN}
              day={incDay(viewStartTime, day)}
              dayWidth={dayWidth}
              textAlign={`${dayCount === 1 ? 'left' : 'center'}`}
              // Gör plats för flerdagsbokningar
              allDayBookingExists={visibleBookingCount > 0}
              allDayBookingCount={visibleBookingCount}
            />
          </EDStickyTableHeaderCell>
        ))}
        {props.allDayBookings && props.allDayBookings.length > 0 && (
          // Flerdagsbokningar som visas i headern
          <KalenderAllDayBookings
            allDayBookings={visibleBookings}
            viewStartTime={viewStartTime}
            dayCount={dayCount}
            dayWidth={dayWidthForAllDayBookings}
            timeWidth={TIME_WIDTH}
            headHeight={HEAD_HEIGHT}
            fallbackStartDate={fallbackDates.start}
            fallbackEndDate={fallbackDates.end}
            onBookingClick={onBookingClick}
            isSmallScreen={isSmallScreen}
          />
        )}
      </EDStickyTableHeader>
      <EDStickyTableRows>
        {range(0, 24).map(hour =>
          range(0, 2).map(halfHour => (
            <EDStickyTableRow key={`h${hour}m${halfHour * 30}`}>
              <EDStickyTableStickyCell>
                <EDCalendarViewClockCell
                  hour={hour}
                  showText={hour > 0 && halfHour === 0}
                />
              </EDStickyTableStickyCell>
              {range(0, dayCount).map(day => {
                // Kalenderceller med bokningar
                const dayStartTime = incDay(viewStartTime, day);
                const dayEndTime = decMin(incDay(dayStartTime), 1);
                const key = dateToStr(dayStartTime);
                const bookings: IBooking[] = bookingColumns[key];
                const cellStartTime = dateAtTime(
                  dayStartTime,
                  hour,
                  (halfHour % 2) * 30
                );
                const cellEndTime = incMin(cellStartTime, 30);
                const selected =
                  props.selStart &&
                  props.selEnd &&
                  timeRangeOverlap(
                    incMin(props.selStart, 15),
                    incMin(props.selEnd, 15),
                    cellStartTime,
                    cellEndTime
                  );
                return (
                  <EDStickyTableCell
                    key={day}
                    col={day}
                    row={hour * 2 + halfHour}
                    selected={selected}
                    onCellSelected={handleCellSelected}
                    onSelectionClick={handleSelectionClick}
                    outside={hour < WORK_HOUR_START || hour >= WORK_HOUR_END}
                  >
                    {hour === 0 && halfHour === 0 && bookings
                      ? bookings.map((booking, index) => (
                          <EDCalendarViewTimeViewBooking
                            key={index}
                            booking={booking}
                            startTime={dayStartTime}
                            endTime={dayEndTime}
                            viewportWidth={props.width}
                            viewportHeight={scrollAreaHeight}
                            columnWidth={dayWidth}
                            selected={false}
                            onBookingClick={onBookingClick}
                            isDragging={isDragging}
                            testId="activity-card"
                          />
                        ))
                      : undefined}
                  </EDStickyTableCell>
                );
              })}
            </EDStickyTableRow>
          ))
        )}
      </EDStickyTableRows>
    </EDStickyTable>
  );
};
